import React, { useState } from 'react';
import { Copy, Check, Mail, Share, ExternalLink } from 'lucide-react';
import { useParadigm } from '../../contexts/ParadigmContext';

const InviteLinkModal = ({
  inviteLink,
  inviteToken,
  email,
  organizationName,
  role,
  onClose,
  onSendEmail,
  emailSentAutomatically = false  // New prop to indicate if email was already sent by backend
}) => {
  const { getRoleLabel } = useParadigm();
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(emailSentAutomatically);
  const [sendingEmail, setSendingEmail] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendEmail = async () => {
    if (sendingEmail || !onSendEmail) return;
    
    setSendingEmail(true);
    try {
      await onSendEmail({
        email,
        inviteLink,
        inviteToken,
        organizationName,
        role
      });
      setEmailSent(true);
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send email. Please try again or share the link manually.');
    } finally {
      setSendingEmail(false);
    }
  };

  const shareViaEmail = () => {
    const subject = `Invitation to join ${organizationName}`;
    const body = `You've been invited to join ${organizationName} as a ${role}.\n\nClick the link below to create your account:\n${inviteLink}\n\nThis invitation is specifically for ${email}. If you did not expect this invitation, please ignore this message.`;
    
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  const testInviteLink = () => {
    window.open(inviteLink, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70] flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Invitation Created Successfully!
                </h3>
                <p className="text-sm text-gray-500">
                  User can now create their account
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-semibold"
            >
              ×
            </button>
          </div>

          {/* Email Sent Notification */}
          {emailSentAutomatically && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Mail className="w-5 h-5 text-green-600 mt-0.5" />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-green-900">
                    Invitation Email Sent Successfully!
                  </h4>
                  <p className="mt-1 text-sm text-green-700">
                    An invitation email has been automatically sent to <strong>{email}</strong> with instructions to create their account.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Invite Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-gray-900">{email}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Role:</span>
                <span className="ml-2 text-gray-900">{getRoleLabel(role)}</span>
              </div>
              <div className="md:col-span-2">
                <span className="font-medium text-gray-700">Organization:</span>
                <span className="ml-2 text-gray-900">{organizationName}</span>
              </div>
            </div>
          </div>

          {/* Invite Link Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invitation Link
              </label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-700 font-mono"
                  />
                </div>
                <button
                  onClick={copyToClipboard}
                  className={`flex items-center px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                    copied
                      ? 'bg-green-50 border-green-300 text-green-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Share this link with {email} to allow them to create their account
              </p>
            </div>

            {/* Token Information (for debugging/admin) */}
            <div className="border-t pt-4">
              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                  Technical Details (for debugging)
                </summary>
                <div className="mt-2 space-y-2 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Invite Token:</span>
                    <span className="ml-2 font-mono bg-gray-100 px-1 rounded">{inviteToken}</span>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <span className="ml-2 text-green-600">Automatically Approved</span>
                  </div>
                </div>
              </details>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 space-y-3">
            {/* Primary Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Send Email Button (if onSendEmail is provided and email wasn't sent automatically) */}
              {onSendEmail && !emailSentAutomatically && (
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || emailSent}
                  className={`flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium transition-colors ${
                    emailSent
                      ? 'bg-green-50 border-green-300 text-green-700 cursor-default'
                      : sendingEmail
                      ? 'bg-gray-50 border-gray-300 text-gray-400 cursor-wait'
                      : 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {emailSent ? 'Email Sent!' : sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
              )}

              {/* Share via Email Client */}
              <button
                onClick={shareViaEmail}
                className={`flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors ${
                  emailSentAutomatically && !onSendEmail ? 'md:col-span-2' : ''
                }`}
              >
                <Share className="w-4 h-4 mr-2" />
                Open in Email App
              </button>
            </div>

            {/* Secondary Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={testInviteLink}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Test Invite Link
              </button>
              
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Next Steps:</p>
                {emailSentAutomatically ? (
                  <ol className="list-decimal list-inside space-y-1">
                    <li>{email} will receive the invitation email with a secure link</li>
                    <li>They'll click the link and create their account</li>
                    <li>Once completed, they'll have access to {organizationName}</li>
                  </ol>
                ) : (
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Copy and share the invitation link with {email}</li>
                    <li>The user will click the link and create their account</li>
                    <li>Once completed, they'll have access to {organizationName}</li>
                  </ol>
                )}
                <p className="mt-2 text-xs">
                  💡 <strong>Testing:</strong> You can test the invite link yourself by opening it in an incognito window.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteLinkModal;